import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, ShieldCheck, ArrowRight, RefreshCcw, XCircle, RotateCcw, ArrowUpRight, FileSignature, KeyRound, ScrollText } from "lucide-react";
import AppShell from "../components/AppShell";
import LegalFooter from "../components/LegalFooter";
import { api } from "../lib/api";
import { invalidateEntitlements } from "../lib/entitlements";

const TIER_ORDER = ["independiente", "empresa", "pro", "privado"];
const TIER_LABEL = { independiente: "Independiente", empresa: "Empresa", pro: "Pro", privado: "Mi Contador Privado" };
const TIER_PRICE = { independiente: 299, empresa: 699, pro: 1499, privado: 3500 };
const TIER_BLURB = {
  independiente: "Para freelancers y creadores",
  empresa: "Para negocios y equipos",
  pro: "Sin límites · Múltiples empresas + Contador Real",
  privado: "Software + IA + Contador Real",
};

/**
 * "Mi Suscripción" — read-only status page for the currently active
 * subscription and recent payment attempts. Cancellation UI lands in Phase 2.
 */
export default function MiSuscripcionPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(""); // "cancel" | "reactivate" | "change:<plan>"
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmChange, setConfirmChange] = useState(null); // target plan id
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments/me", { withCredentials: true });
      setData(r.data);
    } catch (_e) { setData(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const doCancel = async () => {
    setAction("cancel"); setError(""); setNotice("");
    try {
      await api.post("/payments/cancel", {}, { withCredentials: true });
      invalidateEntitlements();
      await load();
      setNotice("Tu suscripción se canceló. Sigues teniendo acceso hasta que termine tu periodo pagado.");
      setConfirmCancel(false);
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos cancelar en este momento. Intenta de nuevo.");
    } finally { setAction(""); }
  };

  const doReactivate = async () => {
    setAction("reactivate"); setError(""); setNotice("");
    try {
      await api.post("/payments/reactivate", {}, { withCredentials: true });
      invalidateEntitlements();
      await load();
      setNotice("¡Tu suscripción está activa de nuevo!");
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos reactivar tu suscripción.");
    } finally { setAction(""); }
  };

  const changePlan = async (targetPlan) => {
    if (!targetPlan) return;
    const currentPT = data?.subscription?.payment_type || "monthly_recurring";
    setConfirmChange(null);
    navigate(`/checkout?plan=${targetPlan}&type=${currentPT}`);
  };

  const sub = data?.subscription;
  const humanStatus = (() => {
    if (!sub) return "—";
    if (sub.trial_active && !sub.is_paid) return `Prueba gratis · quedan ${sub.days_left} día(s)`;
    if (sub.trial_expired && !sub.is_paid) return "Prueba terminada";
    if (sub.status === "cancelled") return "Cancelada (acceso hasta fin del periodo)";
    if (sub.is_paid) return sub.status === "active" ? "Activa" : sub.status;
    return "Sin suscripción";
  })();

  return (
    <AppShell>
      <div className="pricing-page" data-testid="mi-suscripcion-page">
        <section style={{ marginBottom: 26 }}>
          <small className="eyebrow" style={{ color: "#4169e1" }}>MI SUSCRIPCIÓN</small>
          <h1 style={{ font: '900 clamp(28px, 4vw, 40px)/1.05 "Nunito", sans-serif', margin: "8px 0 6px" }}>
            Tu plan de <em style={{ fontStyle: "normal", color: "#4169e1" }}>Mi Contador</em>
          </h1>
          <p style={{ color: "#64748b", font: '500 15px "Nunito", sans-serif', margin: 0 }}>
            Consulta el estado de tu suscripción y tus pagos recientes.
          </p>
        </section>

        <section className="pricing-card" style={{ maxWidth: 640, marginBottom: 24 }} data-testid="mi-suscripcion-card">
          <MiSubAllowance />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: "#eef2ff", color: "#4169e1", display: "grid", placeItems: "center" }}>
              <CreditCard size={22} />
            </div>
            <div>
              <b style={{ font: '900 20px "Nunito", sans-serif', color: "#0f172a" }}>{sub?.plan_name || "—"}</b>
              <div style={{ font: '600 13px "Nunito", sans-serif', color: "#64748b" }}>Estado: {humanStatus}</div>
            </div>
          </div>
          <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Row label="Tipo de pago" value={ptypeLabel(sub?.payment_type)} />
            <Row label="Precio" value={sub?.price_mxn ? `$${sub.price_mxn} MXN/mes + IVA` : "—"} />
            <Row label="Próximo cobro" value={sub?.next_billing_date ? String(sub.next_billing_date).slice(0,10) : (sub?.access_expires_at ? "Sin renovación" : "—")} />
            <Row label="Acceso hasta" value={sub?.access_expires_at ? String(sub.access_expires_at).slice(0,10) : "—"} />
          </dl>

          <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {!sub?.is_paid && (
              <Link to="/pricing" className="pricing-cta primary" data-testid="mi-sub-choose-plan">Elegir plan <ArrowRight size={14} /></Link>
            )}
            {sub?.is_paid && sub?.payment_type === "monthly_manual" && sub?.status !== "cancelled" && (
              <Link to="/pricing" className="pricing-cta" data-testid="mi-sub-renew">Renovar Mi Contador <ArrowRight size={14} /></Link>
            )}
            {sub?.is_paid && sub?.status === "active" && (
              <button
                onClick={() => setConfirmCancel(true)}
                className="pricing-cta"
                style={{ background: "#fff", color: "#b91c1c", border: "1px solid #fecaca" }}
                data-testid="mi-sub-cancel"
                disabled={action === "cancel"}
              >
                <XCircle size={14} /> {action === "cancel" ? "Cancelando…" : "Cancelar suscripción"}
              </button>
            )}
            {sub?.status === "cancelled" && sub?.is_paid && (sub?.payment_type || "").endsWith("_manual") && (
              <button
                onClick={doReactivate}
                className="pricing-cta primary"
                data-testid="mi-sub-reactivate"
                disabled={action === "reactivate"}
              >
                <RotateCcw size={14} /> {action === "reactivate" ? "Reactivando…" : "Reactivar suscripción"}
              </button>
            )}
            {sub?.status === "cancelled" && (sub?.payment_type || "").endsWith("_recurring") && (
              <Link to="/pricing" className="pricing-cta primary" data-testid="mi-sub-resubscribe">Suscribirme de nuevo <ArrowRight size={14} /></Link>
            )}
            <button onClick={load} className="pricing-cta" style={{ background: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0" }} data-testid="mi-sub-refresh">
              <RefreshCcw size={13} /> Actualizar
            </button>
          </div>

          {notice && <div className="pricing-notice success" style={{ marginTop: 14 }} data-testid="mi-sub-notice">{notice}</div>}
          {error && <div className="pricing-notice error" style={{ marginTop: 14 }} data-testid="mi-sub-error">{error}</div>}

          {confirmCancel && (
            <div style={{ marginTop: 16, padding: 16, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12 }} data-testid="mi-sub-confirm-cancel">
              <b style={{ color: "#991b1b", font: '800 14px "Nunito", sans-serif' }}>¿Confirmas cancelar tu suscripción?</b>
              <p style={{ color: "#7f1d1d", font: '500 13px "Nunito", sans-serif', margin: "6px 0 12px" }}>
                {(sub?.payment_type || "").endsWith("_recurring")
                  ? "Ya no se harán cobros automáticos. Sigues con acceso completo hasta el próximo cobro programado."
                  : "Ya no te enviaremos recordatorios de renovación. Conservas tu acceso hasta la fecha de vencimiento."}
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="pricing-cta" style={{ background: "#dc2626", color: "#fff" }} onClick={doCancel} disabled={action === "cancel"} data-testid="mi-sub-cancel-confirm">Sí, cancelar</button>
                <button className="pricing-cta" style={{ background: "#fff", color: "#0f172a", border: "1px solid #e2e8f0" }} onClick={() => setConfirmCancel(false)} data-testid="mi-sub-cancel-abort">Volver</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 18, padding: 12, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 12, color: "#065f46", font: '600 13px "Nunito", sans-serif', display: "flex", alignItems: "center", gap: 8 }}>
            <ShieldCheck size={14} /> Tus datos, facturas, clientes y documentos siempre están a salvo.
          </div>
        </section>

        {sub?.is_paid && sub?.status === "active" && TIER_ORDER.includes(sub?.plan) && (
          <section data-testid="change-plan-section" style={{ marginBottom: 26 }}>
            <h2 style={{ font: '900 20px "Nunito", sans-serif', margin: "0 0 4px", color: "#0f172a" }}>Cambiar de plan</h2>
            <p style={{ font: '500 13px "Nunito", sans-serif', color: "#64748b", margin: "0 0 12px" }}>
              Puedes moverte a otro plan cuando quieras. Se cancelará tu cobro actual y empezará el del nuevo plan.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
              {TIER_ORDER.filter((t) => t !== sub.plan).map((tier) => {
                const isUpgrade = TIER_ORDER.indexOf(tier) > TIER_ORDER.indexOf(sub.plan);
                return (
                  <article key={tier} style={{ padding: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, display: "flex", flexDirection: "column", gap: 10 }} data-testid={`change-plan-card-${tier}`}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <b style={{ font: '900 16px "Nunito", sans-serif', color: "#0f172a" }}>{TIER_LABEL[tier]}</b>
                      <span style={{ font: '800 10px "Nunito", sans-serif', padding: "3px 8px", borderRadius: 999, background: isUpgrade ? "#dbeafe" : "#fef3c7", color: isUpgrade ? "#1e40af" : "#b45309", letterSpacing: 0.4, textTransform: "uppercase" }}>
                        {isUpgrade ? "Subir de plan" : "Bajar de plan"}
                      </span>
                    </div>
                    <div style={{ font: '500 12px "Nunito", sans-serif', color: "#64748b" }}>{TIER_BLURB[tier]}</div>
                    <div>
                      <b style={{ font: '900 22px "Nunito", sans-serif', color: "#0f172a" }}>${TIER_PRICE[tier].toLocaleString("es-MX")}</b>
                      <small style={{ color: "#64748b", marginLeft: 4 }}>+ IVA · MXN/mes</small>
                    </div>
                    <button
                      onClick={() => setConfirmChange(tier)}
                      className={`pricing-cta${isUpgrade ? " primary" : ""}`}
                      disabled={action === `change:${tier}`}
                      data-testid={`change-plan-btn-${tier}`}
                      style={!isUpgrade ? { background: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0" } : {}}
                    >
                      <ArrowUpRight size={14} /> {action === `change:${tier}` ? "…" : `Cambiar a ${TIER_LABEL[tier]}`}
                    </button>
                  </article>
                );
              })}
            </div>
            {confirmChange && (
              <div style={{ marginTop: 16, padding: 16, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12 }} data-testid="mi-sub-confirm-change">
                <b style={{ color: "#1e3a8a", font: '800 14px "Nunito", sans-serif' }}>
                  ¿Confirmas cambiar a {TIER_LABEL[confirmChange]}?
                </b>
                <p style={{ color: "#1e40af", font: '500 13px "Nunito", sans-serif', margin: "6px 0 12px" }}>
                  {(sub?.payment_type || "").endsWith("_recurring")
                    ? `Se cancelará tu cobro automático de ${TIER_LABEL[sub.plan]} y crearemos uno nuevo para ${TIER_LABEL[confirmChange]}. Empezará a cobrarse desde hoy — sin doble cobro.`
                    : `Te llevaremos a Mercado Pago para pagar el nuevo plan. Tu acceso al plan anterior sigue vigente hasta su fecha de vencimiento.`}
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="pricing-cta primary" onClick={() => changePlan(confirmChange)} disabled={action.startsWith("change:")} data-testid="mi-sub-change-confirm">
                    Sí, cambiar plan
                  </button>
                  <button className="pricing-cta" style={{ background: "#fff", color: "#0f172a", border: "1px solid #e2e8f0" }} onClick={() => setConfirmChange(null)} data-testid="mi-sub-change-abort">
                    Volver
                  </button>
                </div>
              </div>
            )}
          </section>
        )}


        <section data-testid="payment-history">
          <h2 style={{ font: '900 20px "Nunito", sans-serif', margin: "0 0 12px", color: "#0f172a" }}>Historial de pagos</h2>
          {loading && <div style={{ color: "#64748b" }}>Cargando…</div>}
          {!loading && (data?.recent_payments || []).length === 0 && (
            <div style={{ color: "#64748b", font: '500 14px "Nunito", sans-serif', padding: 18, background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1", textAlign: "center" }}>
              Aún no hay pagos registrados. Cuando compres tu primer plan lo verás aquí.
            </div>
          )}
          {!loading && (data?.recent_payments || []).length > 0 && (
            <div style={{ overflowX: "auto", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14 }}>
              <table className="mi-sub-history-table" data-testid="payment-history-table" style={{ width: "100%", borderCollapse: "collapse", font: '600 13px "Nunito", sans-serif' }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <Th>Fecha</Th>
                    <Th>Plan</Th>
                    <Th>Tipo de pago</Th>
                    <Th style={{ textAlign: "right" }}>Monto</Th>
                    <Th>Estado</Th>
                    <Th>Referencia</Th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent_payments || []).map((p) => (
                    <tr key={p.id} data-testid={`payment-row-${p.id}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <Td>{String(p.created_at || "").slice(0, 10)}</Td>
                      <Td style={{ textTransform: "capitalize" }}>{p.plan_id}</Td>
                      <Td>{ptypeLabel(p.payment_type)}</Td>
                      <Td style={{ textAlign: "right", fontWeight: 800 }}>${p.amount?.toLocaleString?.("es-MX") || p.amount} {p.currency || "MXN"}</Td>
                      <Td><span style={{ padding: "2px 10px", borderRadius: 999, background: statusBg(p.status), color: statusColor(p.status), font: '800 11px "Nunito", sans-serif', letterSpacing: 0.4, textTransform: "uppercase" }}>{statusLabel(p.status)}</span></Td>
                      <Td style={{ fontFamily: "monospace", fontSize: 11, color: "#475569" }}>{p.mp_payment_id || p.id.slice(0, 12)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <FiscalAuditLog />

        <LegalFooter variant="pricing" />
      </div>
    </AppShell>
  );
}

function FiscalAuditLog() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/fiscal-audit", { withCredentials: true });
      setRows(r.data?.events || []);
    } catch (_e) { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  return (
    <section data-testid="fiscal-audit-section" style={{ marginTop: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ font: '900 20px "Nunito", sans-serif', margin: "0 0 4px", color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
            <ScrollText size={18} /> Bitácora fiscal
          </h2>
          <p style={{ font: '500 13px "Nunito", sans-serif', color: "#64748b", margin: 0 }}>
            Registro auditable de tus subidas de CSD, e.firma y firma de Carta Manifiesto — para que tu contador tenga la evidencia lista si el SAT la pide.
          </p>
        </div>
        <button onClick={load} className="pricing-cta" style={{ background: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0" }} data-testid="fiscal-audit-refresh">
          <RefreshCcw size={13} /> Actualizar
        </button>
      </div>
      {loading && <div style={{ color: "#64748b" }}>Cargando…</div>}
      {!loading && (rows || []).length === 0 && (
        <div style={{ color: "#64748b", font: '500 14px "Nunito", sans-serif', padding: 18, background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1", textAlign: "center" }} data-testid="fiscal-audit-empty">
          Aún no hay eventos fiscales. Cuando subas tu CSD, e.firma o firmes la Carta Manifiesto lo verás aquí.
        </div>
      )}
      {!loading && (rows || []).length > 0 && (
        <div style={{ overflowX: "auto", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14 }}>
          <table data-testid="fiscal-audit-table" style={{ width: "100%", borderCollapse: "collapse", font: '600 13px "Nunito", sans-serif' }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <Th>Fecha</Th>
                <Th>Evento</Th>
                <Th>Empresa</Th>
                <Th>RFC</Th>
                <Th>Detalles</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} data-testid={`fiscal-audit-row-${e.id}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <Td style={{ whiteSpace: "nowrap" }}>{String(e.created_at || "").replace("T", " ").slice(0, 16)} UTC</Td>
                  <Td><EventBadge kind={e.event} /></Td>
                  <Td>{e.business_name || "—"}</Td>
                  <Td style={{ fontFamily: "monospace", fontSize: 12 }}>{e.rfc || "—"}</Td>
                  <Td style={{ color: "#475569" }}>{eventDetails(e)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function EventBadge({ kind }) {
  const map = {
    csd_uploaded:          { label: "CSD subido",           bg: "#dbeafe", fg: "#1e40af", Icon: KeyRound },
    csd_removed:           { label: "CSD retirado",         bg: "#fee2e2", fg: "#991b1b", Icon: KeyRound },
    efirma_uploaded:       { label: "e.firma subida",       bg: "#e0e7ff", fg: "#3730a3", Icon: KeyRound },
    efirma_removed:        { label: "e.firma retirada",     bg: "#fee2e2", fg: "#991b1b", Icon: KeyRound },
    manifest_signed:       { label: "Carta Manifiesto firmada", bg: "#dcfce7", fg: "#065f46", Icon: FileSignature },
    facturapi_provisioned: { label: "Facturapi activado",   bg: "#fef3c7", fg: "#92400e", Icon: ShieldCheck },
    facturapi_reconciled:  { label: "Facturapi sincronizado", bg: "#f1f5f9", fg: "#334155", Icon: RefreshCcw },
  };
  const cfg = map[kind] || { label: kind, bg: "#e2e8f0", fg: "#334155", Icon: ShieldCheck };
  const { Icon } = cfg;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, background: cfg.bg, color: cfg.fg, font: '800 11px "Nunito", sans-serif', letterSpacing: 0.4, textTransform: "uppercase" }}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

function eventDetails(e) {
  const m = e.meta || {};
  if (m.expires_at) return `Vence ${String(m.expires_at).slice(0, 10)}`;
  if (m.source) return `Origen: ${m.source}`;
  if (m.has_manifest !== undefined) return `Cert: ${m.has_certificate ? "sí" : "no"} · Manifiesto: ${m.has_manifest ? "sí" : "no"}`;
  if (m.organization_id) return `Org: ${String(m.organization_id).slice(0, 12)}…`;
  return "—";
}

function MiSubAllowance() {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/invoice-allowance", { withCredentials: true }).then((r) => setA(r.data)).catch(() => setA(null));
  }, []);
  if (!a) return null;
  const pct = a.plan_limit ? Math.min(100, Math.round((a.used / a.plan_limit) * 100)) : 0;
  return (
    <div data-testid="mi-sub-allowance" style={{ padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 12, background: "#f9fafb", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
        <b>Facturas emitidas</b>
        <span data-testid="mi-sub-allowance-count"><b>{a.used}</b> / {a.plan_limit || "—"}{a.extra_credits ? ` (+${a.extra_credits} adicionales)` : ""}</span>
      </div>
      <div style={{ marginTop: 8, height: 6, background: "#f3f4f6", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#dc2626" : pct >= 80 ? "#f59e0b" : "#4169e1" }} />
      </div>
      <small style={{ display: "block", marginTop: 6, color: "#6b7280", fontSize: 11 }}>
        Ciclo hasta {a.cycle_end || "—"}. Los paquetes adicionales no expiran.
      </small>
    </div>
  );
}


function Row({ label, value }) {
  return (
    <div>
      <dt style={{ font: '700 11px "Nunito", sans-serif', color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</dt>
      <dd style={{ margin: 0, font: '800 14px "Nunito", sans-serif', color: "#0f172a" }}>{value}</dd>
    </div>
  );
}

function Th({ children, style }) {
  return <th style={{ padding: "12px 14px", textAlign: "left", font: '800 11px "Nunito", sans-serif', color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, ...style }}>{children}</th>;
}
function Td({ children, style }) {
  return <td style={{ padding: "12px 14px", color: "#0f172a", ...style }}>{children}</td>;
}

function ptypeLabel(t) {
  return {
    monthly_recurring: "Mensual · automático",
    monthly_manual: "Mensual · único",
    yearly_recurring: "Anual · automático",
    yearly_manual: "Anual · único",
  }[t] || "—";
}
function statusLabel(s) {
  return {
    approved: "Aprobado", active: "Activo",
    pending: "Pendiente", created: "Iniciado",
    rejected: "Rechazado", failed: "Fallido",
    cancelled: "Cancelado", refunded: "Reembolsado",
  }[s] || (s || "—");
}
function statusBg(s) {
  if (s === "approved" || s === "active") return "#dcfce7";
  if (s === "pending" || s === "created") return "#fef3c7";
  if (s === "rejected" || s === "failed") return "#fee2e2";
  return "#e2e8f0";
}

function statusColor(s) {
  if (s === "approved" || s === "active") return "#059669";
  if (s === "pending" || s === "created") return "#b45309";
  if (s === "rejected" || s === "failed") return "#b91c1c";
  return "#64748b";
}
